var Type = require('./Type');
var Alignment = require('../../style/Alignment');

/**
 * The Text class represents textual information without any
 * particular formatting. It provides font and sizing details that
 * can be used by the Styles class.
 *
 * @param {Object} [oConfig] - Configuration object for this Text type
 * @param {Object} [oConfig.font] - Font configuration object
 * @param {number} [oConfig.font.size] - Font size
 * @param {string} [oConfig.font.name] - Font name i.E. Arial, Verdana, etc.
 * @param {Object} [oConfig.fill] - Cell fill configuration object
 * @param {string} [oConfig.fill.color] - Cell fill background color
 * @param {string} [oConfig.fill.type] - Cell fill type
 * @param {string} [oConfig.template] - Text template for this Type
 * @param {string} [oConfig.hierarchyLevel] - Property name that is used for hierarchy level indentation
 * @param {boolean} [oConfig.wrap] - Indicates the wrapping behavior of the text cell
 * @constructor
 */
function Text(oConfig) {
	Type.call(this, oConfig);

	this.font = oConfig ? oConfig.font : null;
	this.fill = oConfig ? oConfig.fill : null;
	this.template = oConfig ? oConfig.template : null;
	this.hierarchyLevel = oConfig ? oConfig.hierarchyLevel : null;
	this.wrap = oConfig ? oConfig.wrap : false;

	this._styleIds = [];
}

Text.prototype = Object.create(Type.prototype);
Text.prototype.constructor = Text;

Text.DEFAULT = {
	font: {
		size: 11,
		name: 'Arial'
	}
};

/**
 * Ensures that all line breaks use CR + LF.
 *
 * @param {string} sValue - Text that may contain line breaks
 * @return {string} - Initial text with replaced line breaks
 */
Text.convertLineBreaks = function(sValue) {
	var compliantLineBreak = String.fromCharCode(13, 10);

	return sValue.replace(/\r/g, '').replace(/\n/g, compliantLineBreak);
};

/**
 * This method creates the XLSX specific style configuration depending on the cell content
 *
 * @param {Object} oData - Line item that will be used to evaluate the cell content
 * @returns {Object} - Style configuration used by the Styles class
 * @protected
 */
Text.prototype._createConfiguration = function(oData) {
	var config;

	config = Object.getPrototypeOf(Text.prototype)._createConfiguration.call(this);
	config.fill = this.fill;
	config.font = this.font;

	/* Overwrites existing alignment for a proper indent */
	if (this.hierarchyLevel) {
		config.align = {
			horizontal: Alignment.horizontal.LEFT,
			indent: 2 * oData[this.hierarchyLevel]
		};
	}

	/* Wrapping can be applied on hierarchies with indentation */
	if (this.wrap) {
		config.align = config.align || {};

		config.align.wrap = this.wrap;
	}

	return config;
};

/**
 * Returns the assigned ID of the Type specific Style that is
 * registered in the Styles class.
 *
 * @param {Object} oData - Business Object that contains the property for hierarchy levels
 * @returns {number} - ID of the registered Style
 */
Text.prototype.getStyleId = function(oData) {
	var styleId, level;

	/* Handle simple styling without hierarchy information */
	if (!this.hierarchyLevel) {
		return Object.getPrototypeOf(Text.prototype).getStyleId.call(this);
	}

	if (!oData) {
		return 0;
	}

	level = oData[this.hierarchyLevel];
	styleId = this._styleIds[level];

	if (typeof styleId === 'undefined') {
		this._styleIds[level] = this.styles.addStyle(this._createConfiguration(oData));
	}

	return this._styleIds[level];
};

/**
 * Returns the data value of the business object that is bound by
 * this type. If a template is configured, it will automatically fill
 * the template with the values from the business object and returns
 * the processed string.
 *
 * @param {string|Array} properties - Name of the property or array of properties that shall be accessed.
 * @param {Object} oData - Business object that contains the data.
 *
 * @returns {string|number|boolean} - The value of the given property after type specific processing.
 */
Text.prototype.getValue = function(properties, oData) {
	var match, regex, result, skip, value;

	if (!this.template) {
		value = typeof properties === 'string' ? oData[properties] : oData[properties[0]];

		value = this.wrap ? Text.convertLineBreaks(value) : value;

		return value;
	}

	// Obtain if the further processing should be skipped due to missing values for the template
	skip = properties.every(function(property) {
		return oData[property] === null
			|| typeof oData[property] === 'undefined'
			|| (typeof oData[property] === 'string' && oData[property] === '');
	});

	if (skip) {
		return '';
	}

	result = this.template;
	regex = /{([0-9]+)}/g;

	match = regex.exec(this.template);
	while (match !== null) {

		value = oData[properties[match[1]]];

		// Overwrite empty or undefined values by an empty string
		if (value === null || typeof value === 'undefined') {
			value = '';
		}

		// Replaces the matched placeholder by the indexed property value
		result = result.replace(match[0], value);

		match = regex.exec(this.template);
	}

	return result;
};

module.exports = Text;


//////////////////
// WEBPACK FOOTER
// ./core/xlsx/src/binding/type/Text.js
// module id = 16
// module chunks = 0